/**
 * AgendamientoSucursales.jsx · v101.144.0
 * Integraciones › Agendamiento y WhatsApp por sucursal.
 * Por sucursal: prender / apagar el agendamiento automático (A2) y la confirmación del cliente por WhatsApp.
 * El interruptor maestro del agendamiento automático sigue arriba: apagado = no se agenda en ninguna sucursal.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Bot, MessageCircle, Loader2, RefreshCw, Search, History, Info, Sparkles } from "lucide-react";
import Layout from "../components/Layout";
import api from "../lib/api";
import { Switch } from "../components/ui/switch";
import A2AutopilotSwitch from "../components/A2AutopilotSwitch";

const BASE = "/v1/integraciones/sucursales-automatizacion";
const LABEL = { auto_schedule: "Agendamiento automático", whatsapp_confirm: "Confirmación por WhatsApp", ai_fiscalization: "Autofiscalización por IA" };
const FIELDS = ["auto_schedule", "whatsapp_confirm", "ai_fiscalization"];   // v101.146.0
const SHORT = { auto_schedule: "agendamiento", whatsapp_confirm: "WhatsApp", ai_fiscalization: "IA" };

export default function AgendamientoSucursales() {
  const [data, setData] = useState({ items: [], master: {}, can_edit: false });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [q, setQ] = useState("");
  const [showLog, setShowLog] = useState(false);
  const [log, setLog] = useState([]);

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get(BASE);
      setData(d || { items: [], master: {}, can_edit: false });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo cargar la configuración por sucursal");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadLog = useCallback(async () => {
    try {
      const { data: d } = await api.get(`${BASE}/cambios`);
      setLog(d?.items || []);
    } catch { setLog([]); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (showLog) loadLog(); }, [showLog, loadLog]);

  const items = useMemo(() => {
    const t = q.trim().toLowerCase();
    return (data.items || []).filter(b => !t || String(b.branch_name || b.branch_office_id).toLowerCase().includes(t));
  }, [data.items, q]);

  const setOne = async (b, field, value) => {
    const verb = value ? "PRENDER" : "APAGAR";
    if (!window.confirm(`¿${verb} «${LABEL[field]}» en ${b.branch_name || b.branch_office_id}?`)) return;
    setBusy(`${b.branch_office_id}-${field}`);
    try {
      await api.put(`${BASE}/${b.branch_office_id}`, { [field]: value });
      toast.success(`${LABEL[field]} ${value ? "prendido" : "apagado"} · ${b.branch_name || b.branch_office_id}`);
      await load();
      if (showLog) loadLog();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo guardar");
    } finally { setBusy(null); }
  };

  const setAll = async (field, value) => {
    const verb = value ? "PRENDER" : "APAGAR";
    if (!window.confirm(`¿${verb} «${LABEL[field]}» en TODAS las sucursales?`)) return;
    setBusy(`all-${field}`);
    try {
      const { data: d } = await api.put(`${BASE}/todas`, { [field]: value });
      toast.success(`${LABEL[field]} ${value ? "prendido" : "apagado"} en todas (${d?.changed_branches ?? 0} cambiadas)`);
      await load();
      if (showLog) loadLog();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo guardar");
    } finally { setBusy(null); }
  };

  const masterOff = data.master && data.master.enabled === false;
  const canEdit = !!data.can_edit;

  return (
    <Layout>
      <div className="max-w-5xl mx-auto p-4 space-y-4" data-testid="agendamiento-sucursales-page">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-lg font-bold text-[#0A3747]">Agendamiento, WhatsApp e IA por sucursal</h1>
            <p className="text-xs text-slate-500 max-w-2xl">
              Prenda o apague, por sucursal, el agendamiento automático de OTs, la confirmación del cliente por
              WhatsApp y la autofiscalización por IA de los técnicos. Una sucursal sin agendamiento automático deja sus
              OTs en «Nuevas» para agendar a mano.
            </p>
          </div>
          <button onClick={() => { setLoading(true); load(); }} className="px-3 py-1.5 rounded-lg border border-slate-200 text-xs font-bold text-slate-600 flex items-center gap-1.5 hover:bg-slate-50" data-testid="bs-refresh">
            <RefreshCw size={12} /> Actualizar
          </button>
        </div>

        <A2AutopilotSwitch />
        {masterOff && (
          <div className="rounded-xl bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800 flex items-start gap-2" data-testid="bs-master-off">
            <Info size={14} className="shrink-0 mt-0.5" />
            <span>El agendamiento automático está <b>apagado para todo el sistema</b> (interruptor maestro). Mientras siga
              apagado no se agenda en ninguna sucursal, aunque aquí esté prendida.</span>
          </div>
        )}

        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={q} onChange={e => setQ(e.target.value)} placeholder="Buscar sucursal"
              className="pl-7 pr-3 py-1.5 rounded-lg border border-slate-200 text-xs w-56" data-testid="bs-search" />
          </div>
          {canEdit && (
            <div className="flex items-center gap-1.5 flex-wrap text-[11px]">
              <span className="text-slate-500 font-bold">Todas:</span>
              {FIELDS.map(f => (
                [true, false].map(v => (
                  <button key={`${f}-${v}`} onClick={() => setAll(f, v)} disabled={!!busy}
                    className={`px-2 py-1 rounded-md border text-[11px] font-bold disabled:opacity-50 ${v ? "border-emerald-200 text-emerald-700 hover:bg-emerald-50" : "border-red-200 text-red-700 hover:bg-red-50"}`}
                    data-testid={`bs-all-${f}-${v ? "on" : "off"}`}>
                    {v ? "Prender" : "Apagar"} {SHORT[f]}
                  </button>
                ))
              ))}
            </div>
          )}
        </div>

        {loading ? (
          <div className="text-center py-12 text-slate-500 text-xs"><Loader2 size={14} className="animate-spin inline mr-2" />Cargando…</div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto" data-testid="bs-table">
            <table className="w-full text-xs min-w-[720px]">
              <thead className="bg-slate-50 text-slate-500 uppercase text-[10px] tracking-wider">
                <tr>
                  <th className="px-3 py-2 text-left font-bold">Sucursal</th>
                  <th className="px-3 py-2 text-center font-bold">OTs abiertas</th>
                  <th className="px-3 py-2 text-center font-bold">Agendadas desde hoy</th>
                  <th className="px-3 py-2 text-center font-bold">Técnicos</th>
                  <th className="px-3 py-2 text-center font-bold"><span className="inline-flex items-center gap-1"><Bot size={11} />Agendamiento automático</span></th>
                  <th className="px-3 py-2 text-center font-bold"><span className="inline-flex items-center gap-1"><MessageCircle size={11} />Confirmación WhatsApp</span></th>
                  <th className="px-3 py-2 text-center font-bold"><span className="inline-flex items-center gap-1"><Sparkles size={11} />Autofiscalización IA</span></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {items.map(b => (
                  <tr key={b.branch_office_id} data-testid={`bs-row-${b.branch_office_id}`}>
                    <td className="px-3 py-2">
                      <div className="font-bold text-[#0A3747]">{b.branch_name || `Sucursal ${b.branch_office_id}`}</div>
                      {b.updated_by && <div className="text-[10px] text-slate-400">Cambiado por {b.updated_by}{b.updated_at ? ` · ${new Date(b.updated_at).toLocaleString("es-EC")}` : ""}</div>}
                    </td>
                    <td className="px-3 py-2 text-center tabular-nums">{b.abiertas}</td>
                    <td className="px-3 py-2 text-center tabular-nums">{b.agendadas}</td>
                    <td className="px-3 py-2 text-center tabular-nums">{b.tecnicos ?? "—"}</td>
                    {FIELDS.map(f => (
                      <td key={f} className="px-3 py-2 text-center">
                        <div className="inline-flex items-center gap-2">
                          {busy === `${b.branch_office_id}-${f}` ? <Loader2 size={13} className="animate-spin text-slate-400" /> : (
                            <Switch checked={!!b[f]} disabled={!canEdit || !!busy}
                              onCheckedChange={v => setOne(b, f, v)} data-testid={`bs-${f}-${b.branch_office_id}`} />
                          )}
                          <span className={`text-[10px] font-bold ${b[f] ? (f === "auto_schedule" && masterOff ? "text-amber-600" : "text-emerald-700") : "text-red-600"}`}>
                            {b[f] ? (f === "auto_schedule" && masterOff ? "Prendido (maestro apagado)" : "Prendido") : "Apagado"}
                          </span>
                        </div>
                      </td>
                    ))}
                  </tr>
                ))}
                {items.length === 0 && (
                  <tr><td colSpan={7} className="text-center py-8 text-slate-400 italic">Sin sucursales</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        <div className="text-[10.5px] text-slate-500 space-y-1">
          <div><b>Agendamiento automático:</b> el agente A2 asigna técnico y fecha a las OTs nuevas de la sucursal. Apagado: las
            OTs quedan en «Nuevas» y se agendan a mano. No cambia las OTs ya agendadas.</div>
          <div><b>Confirmación por WhatsApp:</b> mensajes al cliente de la sucursal (aviso de cita del agendamiento automático,
            propuesta de cita, zona llena y aviso de retraso). Apagado: no se generan.</div>
          <div><b>Autofiscalización por IA:</b> se aplica a los técnicos de la sucursal (su sucursal en Usuarios). Apagado:
            la app sigue pidiendo las fotos del formulario, pero sin validación por IA (no aparece «Validación IA
            activa»). Los técnicos lo ven al volver a abrir el formulario con internet.</div>
        </div>

        <div>
          <button onClick={() => setShowLog(v => !v)} className="text-xs font-bold text-[#0A3747] flex items-center gap-1.5" data-testid="bs-log-toggle">
            <History size={12} /> {showLog ? "Ocultar" : "Ver"} historial de cambios
          </button>
          {showLog && (
            <div className="mt-2 bg-white border border-slate-200 rounded-xl overflow-x-auto" data-testid="bs-log">
              <table className="w-full text-[11px] min-w-[520px]">
                <tbody className="divide-y divide-slate-100">
                  {log.map((c, i) => (
                    <tr key={i}>
                      <td className="px-3 py-1.5 text-slate-500 whitespace-nowrap">{c.changed_at ? new Date(c.changed_at).toLocaleString("es-EC") : "—"}</td>
                      <td className="px-3 py-1.5 font-bold text-[#0A3747]">{c.branch_name || c.branch_office_id}</td>
                      <td className="px-3 py-1.5">{c.field_label}: {c.old_value ? "Prendido" : "Apagado"} → <b>{c.new_value ? "Prendido" : "Apagado"}</b></td>
                      <td className="px-3 py-1.5 text-slate-500">{c.changed_by || "—"}{c.note ? ` · ${c.note}` : ""}</td>
                    </tr>
                  ))}
                  {log.length === 0 && <tr><td className="px-3 py-4 text-center text-slate-400 italic">Sin cambios registrados</td></tr>}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
